import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { LogOut, ChevronDown, Menu, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

function LangToggle() {
  const { lang, setLang } = useI18n();
  return (
    <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-0.5" data-testid="lang-toggle">
      {["el", "en"].map((code) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          data-testid={`lang-${code}`}
          className={`px-3 py-1 text-xs font-semibold tracking-wide rounded-full transition ${
            lang === code ? "bg-[#0A192F] text-white" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const hideOnApp = loc.pathname.startsWith("/dashboard") || loc.pathname.startsWith("/matters") || loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/settings") || loc.pathname.startsWith("/pricing") || loc.pathname.startsWith("/billing");
  const onMarketing = !hideOnApp;

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Lock body scroll while mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [mobileOpen]);

  return (
    <header className="sticky top-0 z-40 glass-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <img
            src="/dikigoros-logo.png"
            alt="Dikigoros.io"
            className="h-9 sm:h-10 w-auto"
            width={1600}
            height={451}
          />
        </Link>

        {onMarketing && (
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="/#features" className="hover:text-slate-900 transition" data-testid="nav-features">{t("nav.features")}</a>
            <a href="/#how" className="hover:text-slate-900 transition" data-testid="nav-how">{t("nav.how")}</a>
            <a href="/#pricing" className="hover:text-slate-900 transition" data-testid="nav-pricing">{t("nav.pricing")}</a>
          </nav>
        )}

        {/* Desktop right cluster */}
        <div className="hidden md:flex items-center gap-3">
          <LangToggle />
          {!user ? (
            <>
              <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900" data-testid="nav-login">{t("nav.login")}</Link>
              <Link to="/signup" className="btn-primary rounded-full px-4 py-2 text-sm font-medium" data-testid="nav-signup">{t("nav.signup")}</Link>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="user-menu">
                  <div className="h-6 w-6 rounded-full bg-[#0A192F] text-white flex items-center justify-center text-xs font-semibold">
                    {(user.full_name || user.email).charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-slate-700">{user.full_name?.split(" ")[0] || user.email}</span>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">{t("nav.dashboard")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/pricing")} data-testid="menu-pricing">{t("nav.pricing")}</DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">Admin</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Mobile right cluster: lang + hamburger */}
        <div className="flex md:hidden items-center gap-2">
          <LangToggle />
          <button
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 bg-white text-[#0A192F] hover:bg-slate-50 transition"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden absolute left-0 right-0 top-16 z-50 mobile-menu-in" data-testid="mobile-menu">
          <div className="mx-3 mt-2 rounded-2xl bg-white border border-slate-200 shadow-[0_20px_50px_rgba(10,25,47,0.18)] overflow-hidden">
            {onMarketing && (
              <nav className="flex flex-col py-2">
                <a href="/#features" onClick={() => setMobileOpen(false)} className="px-5 py-3 text-[15px] text-slate-700 hover:bg-slate-50 border-b border-slate-100" data-testid="mobile-nav-features">{t("nav.features")}</a>
                <a href="/#how" onClick={() => setMobileOpen(false)} className="px-5 py-3 text-[15px] text-slate-700 hover:bg-slate-50 border-b border-slate-100" data-testid="mobile-nav-how">{t("nav.how")}</a>
                <a href="/#pricing" onClick={() => setMobileOpen(false)} className="px-5 py-3 text-[15px] text-slate-700 hover:bg-slate-50" data-testid="mobile-nav-pricing">{t("nav.pricing")}</a>
              </nav>
            )}
            <div className="px-5 py-4 bg-slate-50/60 border-t border-slate-100">
              {!user ? (
                <div className="flex flex-col gap-2">
                  <Link to="/login" className="btn-ghost rounded-full px-5 py-2.5 text-sm text-center" data-testid="mobile-nav-login">{t("nav.login")}</Link>
                  <Link to="/signup" className="btn-primary rounded-full px-5 py-2.5 text-sm text-center" data-testid="mobile-nav-signup">{t("nav.signup")}</Link>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
                    <div className="h-9 w-9 rounded-full bg-[#0A192F] text-white flex items-center justify-center text-sm font-semibold">
                      {(user.full_name || user.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="text-sm">
                      <div className="font-semibold text-slate-900">{user.full_name || user.email.split("@")[0]}</div>
                      <div className="text-xs text-slate-500">{user.email}</div>
                    </div>
                  </div>
                  <button onClick={() => { nav("/dashboard"); setMobileOpen(false); }} className="text-left px-2 py-2.5 text-sm text-slate-700 hover:bg-white rounded-lg" data-testid="mobile-menu-dashboard">{t("nav.dashboard")}</button>
                  <button onClick={() => { nav("/pricing"); setMobileOpen(false); }} className="text-left px-2 py-2.5 text-sm text-slate-700 hover:bg-white rounded-lg" data-testid="mobile-menu-pricing">{t("nav.pricing")}</button>
                  {user.role === "admin" && (
                    <button onClick={() => { nav("/admin"); setMobileOpen(false); }} className="text-left px-2 py-2.5 text-sm text-slate-700 hover:bg-white rounded-lg" data-testid="mobile-menu-admin">Admin</button>
                  )}
                  <button onClick={async () => { await logout(); setMobileOpen(false); nav("/"); }} className="text-left px-2 py-2.5 text-sm text-[#8B1E3F] hover:bg-white rounded-lg inline-flex items-center gap-2" data-testid="mobile-menu-logout">
                    <LogOut className="h-4 w-4" /> {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Backdrop */}
          <div className="fixed inset-0 top-16 -z-10 bg-slate-900/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        </div>
      )}
    </header>
  );
}
